import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ClipboardCheck, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const R = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LeavePayslipCheck({ headers }) {
  const [data, setData] = useState(null);
  const [onlyIssues, setOnlyIssues] = useState(true);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/leave/payslip-check?months=3`, { headers })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load payslip check'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter((r) => !onlyIssues || !r.ok);
  return (
    <Card data-testid="leave-payslip-check">
      <CardHeader className="pb-3 flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-indigo-600" />
          Payslip Leave Check — {data?.months?.join(', ') || '…'}
          {data && <span className={`text-xs font-semibold ${data.mismatches ? 'text-rose-600' : 'text-emerald-700'}`} data-testid="payslip-check-mismatch-count">
            {data.mismatches ? `${data.mismatches} payslip(s) differ` : 'All payslips match'}
          </span>}
        </CardTitle>
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} data-testid="payslip-check-only-issues" />Only differences</label>
          <Button size="sm" variant="outline" onClick={load} data-testid="payslip-check-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <p className="px-4 pb-2 text-xs text-muted-foreground">Read-only. Expected = register LEAVE days + approved requests, split paid/unpaid against the balance. Nothing on the payslips is changed.</p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead><TableHead>Staff</TableHead>
              <TableHead className="text-center">Register LEAVE</TableHead>
              <TableHead className="text-center">Expected paid / unpaid</TableHead>
              <TableHead className="text-center">On payslip paid / unpaid</TableHead>
              <TableHead className="text-right">Pay difference</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.payslip_id} className={r.ok ? '' : 'bg-rose-50/60'} data-testid={`payslip-check-row-${r.payslip_id}`}>
                <TableCell className="font-mono text-sm">{r.month}</TableCell>
                <TableCell className="font-medium">{r.staff_name}</TableCell>
                <TableCell className="text-center">{r.register_leave_days}{r.register_only_days > 0 && <div className="text-[10px] text-amber-700">{r.register_only_days} without request</div>}</TableCell>
                <TableCell className="text-center font-mono">{r.expected_paid} / {r.expected_unpaid}</TableCell>
                <TableCell className="text-center font-mono">{r.payslip_paid} / {r.payslip_unpaid}</TableCell>
                <TableCell className={`text-right font-mono ${r.pay_difference > 0 ? 'text-emerald-700' : r.pay_difference < 0 ? 'text-rose-700' : 'text-muted-foreground'}`} data-testid={`payslip-check-diff-${r.payslip_id}`}>
                  {r.ok ? 'OK' : `${r.pay_difference > 0 ? 'Underpaid ' : r.pay_difference < 0 ? 'Overpaid ' : ''}${R(Math.abs(r.pay_difference))}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && rows.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No differences found.</div>}
      </CardContent>
    </Card>
  );
}
